import { Container } from '@ag1/ui';
import {
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53
} from '@portal/config/base';
import { BannerUpdatePaymentV2 } from '@portal/processes/SubscriptionPageV2/components/BannerUpdatePaymentV2';
import { useRouter } from 'next/router';
import { useSubscriptionListMissingPaymentV2 } from 'processes/SubscriptionPageV2/hooks/useMissingPaymentBannerV2';
import { useSubscriptionListDeclinedPaymentV2 } from 'processes/SubscriptionPageV2/hooks/usePaymentDeclinedBannerV2';
import { useSubscriptionListExpiringPaymentV2 } from 'processes/SubscriptionPageV2/hooks/usePaymentExpiredBannerV2';
import { useSubscriptionListPromotionV2 } from 'processes/SubscriptionPageV2/hooks/usePromotionalBannerV2';
import { useCallback, useEffect, useMemo } from 'react';
import { SUBSCRIPTION_PAGE_TYPE } from 'schema/subscription/pageType';
import { ModalV53Notification } from 'shared/components/ModalV53Notification';
import { useToggle } from 'shared/hooks/useToggle';
import { showNotification } from 'shared/ui/Notification';
import {
  formatSubscriptionListData,
  getLineItemIdByVariantId,
  getShopifyContractId
} from 'shared/utils/subscriptionV2Helpers';
import { useAtomValueV53NotificationShown } from 'state/atoms/v53NotificationShown';
import { useDoubleSubscriptionRequestV2 } from 'state/hooks/useDoubleSubscriptionRequestV2';
import { useStoreValue } from 'state/hooks/useStore';
import { SubscriptionsResponseV2 } from 'state/internal/getSubscriptions/types';
import { useQueryGetPromotionalBannerData } from 'state/studio/promotionalBannerData/getPromotionalBannerData';
import { useIsShopPageAvailable } from '@portal/processes/ShopV2/hooks/useIsShopPageAvailable';
import { BannerShop } from '../BannerShop/BannerShop';
import { ConnectPaymentMethodBannerV2 } from '../ConnectPaymentMethodBannerV2';
import DoubleSubscriptionPromotionalModalV2 from '../DoubleSubscriptionPromotionalModalV2';
import PaymentMethodModalV2 from '../PaymentMethodModalV2';
import { PromotionalBannerV2 } from '../PromotionalBannerV2';
import styles from './HomeBanners.module.scss';

interface Props {
  subscriptionsList: SubscriptionsResponseV2;
}

const { SUBSCRIPTION_LIST } = SUBSCRIPTION_PAGE_TYPE;

export const HomeBanners = ({ subscriptionsList }: Props) => {
  const store = useStoreValue();
  const { query } = useRouter();

  const { data: promotionalBannerData, isPending: isLoadingBannerData } =
    useQueryGetPromotionalBannerData();
  const { doubleSubscriptionRequestV2, isPending: isLoadingDoubleSubscription } =
    useDoubleSubscriptionRequestV2();

  const { isShopPageAvailable: isShopPageAvailableV2 } = useIsShopPageAvailable();

  const v53NotificationShown = useAtomValueV53NotificationShown();

  const [isPromotionalSidePanelV2Open, setIsPromotionalSidePanelV2Open] = useToggle(false);
  const [isEditPaymentModalV2Open, setIsEditPaymentModalV2Open] = useToggle(false);
  const [isAddPaymentMethodModalV2Open, setIsAddPaymentMethodModalV2Open] = useToggle(false);
  useEffect(() => {
    if (query?.action === 'update-payment') {
      setIsEditPaymentModalV2Open(true);
    }

    if (query?.action === 'add-payment') {
      setIsAddPaymentMethodModalV2Open(true);
    }
  }, [query, setIsEditPaymentModalV2Open, setIsAddPaymentMethodModalV2Open]);

  // --------- home banners calculations -----------

  // promotional banner
  const defaultPromotionalBannerDataV2 = useSubscriptionListPromotionV2({
    subscriptions: formatSubscriptionListData(subscriptionsList),
    promotionalBannerData
  });
  const showPromotionalBannerV2 = useMemo(
    () =>
      !v53NotificationShown &&
      defaultPromotionalBannerDataV2?.hasCompatibleSubscription &&
      defaultPromotionalBannerDataV2?.targetSubscriptions?.length,
    [defaultPromotionalBannerDataV2, v53NotificationShown]
  );

  const showPromotionalSubscriptionSidePanelV2 = useMemo(
    () =>
      defaultPromotionalBannerDataV2?.targetSubscriptions &&
      defaultPromotionalBannerDataV2?.targetSubscriptions?.length > 1,
    [defaultPromotionalBannerDataV2]
  );
  // shop banner
  const showShopBannerV2 = useMemo(
    () => !v53NotificationShown && isShopPageAvailableV2 && !showPromotionalBannerV2,
    [isShopPageAvailableV2, showPromotionalBannerV2, v53NotificationShown]
  );

  // expiring payment banner
  const expiredPaymentBannerDataV2 = useSubscriptionListExpiringPaymentV2({
    subscriptions: subscriptionsList
  });

  const showExpiringPaymentBannerV2 = useMemo(
    () => expiredPaymentBannerDataV2.hasCompatibleSubscription,
    [expiredPaymentBannerDataV2]
  );

  // declined payment banner
  const declinedPaymentBannerDataV2 = useSubscriptionListDeclinedPaymentV2({
    subscriptions: subscriptionsList
  });

  const showDeclinedPaymentBannerV2 = useMemo(
    () => declinedPaymentBannerDataV2.hasCompatibleSubscription,
    [declinedPaymentBannerDataV2]
  );

  // missing payment banner
  const missingPaymentBannerDataV2 = useSubscriptionListMissingPaymentV2({
    subscriptions: subscriptionsList
  });

  const showMissingPaymentBannerV2 = useMemo(
    () => missingPaymentBannerDataV2.hasCompatibleSubscription,
    [missingPaymentBannerDataV2]
  );

  // ------- dynamic data --------

  const problematicPaymentMethodId = useMemo(() => {
    if (showDeclinedPaymentBannerV2 && !showMissingPaymentBannerV2)
      return declinedPaymentBannerDataV2.paymentMethodId;

    if (showMissingPaymentBannerV2) return missingPaymentBannerDataV2.missingPaymentId;

    return '';
  }, [
    declinedPaymentBannerDataV2,
    showDeclinedPaymentBannerV2,
    missingPaymentBannerDataV2,
    showMissingPaymentBannerV2
  ]);

  // ------- click handlers --------

  const restoreUrl = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('action');
    window.history.pushState({}, document.title, url);
  }, []);

  const handleClosePaymentExpiredModalV2 = useCallback(() => {
    setIsEditPaymentModalV2Open(false);
    restoreUrl();
  }, [setIsEditPaymentModalV2Open, restoreUrl]);

  const handleCloseMissingPaymentMethodModalV2 = useCallback(() => {
    setIsAddPaymentMethodModalV2Open(false);
    restoreUrl();
  }, [setIsAddPaymentMethodModalV2Open, restoreUrl]);

  const handlePromotionalBannerClick = useCallback(() => {
    if (
      !defaultPromotionalBannerDataV2?.hasCompatibleSubscription ||
      !defaultPromotionalBannerDataV2?.bannerData
    )
      return;

    // if only 1 subscription is AG1 single sub, directly update that subscription
    if (defaultPromotionalBannerDataV2?.targetSubscriptions?.length === 1) {
      const subscriptionData = defaultPromotionalBannerDataV2?.targetSubscriptions[0];
      const contractId = getShopifyContractId(subscriptionData.shopify_contract_id);
      const singleVariantId = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store].default;
      const singleVariantFreeShipping = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID[store].freeShipping;
      const singleVariantIdV53 = SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].default;
      const singleVariantIdV53Tropical =
        SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store].tropical;
      const singleVariantLineItemId = getLineItemIdByVariantId(subscriptionData, singleVariantId);
      const singleVariantLineItemIdV53 = getLineItemIdByVariantId(
        subscriptionData,
        singleVariantIdV53
      );
      const singleVariantLineItemIdV53Tropical = singleVariantIdV53Tropical
        ? getLineItemIdByVariantId(subscriptionData, singleVariantIdV53Tropical)
        : '';

      const singleVariantLineItemIdFreeShipping = singleVariantFreeShipping
        ? getLineItemIdByVariantId(subscriptionData, singleVariantFreeShipping)
        : '';

      const lineItemId =
        singleVariantLineItemId ||
        singleVariantLineItemIdV53 ||
        singleVariantLineItemIdV53Tropical ||
        singleVariantLineItemIdFreeShipping;

      if (!lineItemId) return;

      let isV53 = false;
      if (singleVariantLineItemIdV53 || singleVariantLineItemIdV53Tropical) isV53 = true;

      doubleSubscriptionRequestV2({
        contractId,
        isV53,
        isTropical: Boolean(singleVariantLineItemIdV53Tropical),
        isFreeShipping: Boolean(singleVariantLineItemIdFreeShipping),
        singleVariantLineItemId: lineItemId,
        ...(defaultPromotionalBannerDataV2.bannerData && {
          onSuccess: () => {
            showNotification('mutationSuccess', {
              title: defaultPromotionalBannerDataV2.bannerData?.successMessage
            });
          }
        })
      });
    } else {
      // if more than 1 subscriptions is AG1 single sub, open side panel
      setIsPromotionalSidePanelV2Open(true);
    }
  }, [
    store,
    defaultPromotionalBannerDataV2,
    doubleSubscriptionRequestV2,
    setIsPromotionalSidePanelV2Open
  ]);

  // if payment method is not valid - do not show any of the promotional banners
  const needToUpdatePayment =
    showExpiringPaymentBannerV2 || showDeclinedPaymentBannerV2 || showMissingPaymentBannerV2;

  if (isLoadingBannerData) {
    return null;
  }

  return (
    <>
      {needToUpdatePayment ? (
        <>
          {/* declined payment method banner */}
          {showDeclinedPaymentBannerV2 && (
            <div className={styles.banners}>
              <Container>
                <BannerUpdatePaymentV2
                  page={SUBSCRIPTION_LIST}
                  hasDeclinedPayment={showDeclinedPaymentBannerV2}
                />
              </Container>
            </div>
          )}
          {/* expiring payment method banner */}
          {showExpiringPaymentBannerV2 && !showDeclinedPaymentBannerV2 && (
            <div className={styles.banners}>
              <Container>
                <BannerUpdatePaymentV2
                  page={SUBSCRIPTION_LIST}
                  hasExpiringPayment={showExpiringPaymentBannerV2}
                />
              </Container>
            </div>
          )}

          {/* missing payment method banner */}
          {/* currently there is no official way to trigger this from shopify flow */}
          {showMissingPaymentBannerV2 && !showDeclinedPaymentBannerV2 && (
            <div className={styles.banners}>
              <Container>
                <ConnectPaymentMethodBannerV2
                  isMissingPayment={showMissingPaymentBannerV2}
                  onActionClick={() => setIsEditPaymentModalV2Open(true)}
                />
              </Container>
            </div>
          )}
        </>
      ) : (
        <>
          {/* shop banner */}
          {showShopBannerV2 && (
            <div className={styles.bannersColored}>
              <Container>
                <BannerShop type="shopify" page={SUBSCRIPTION_LIST} />
              </Container>
            </div>
          )}
          {/* promotional banner (double sub) */}
          {showPromotionalBannerV2 && (
            <div className={styles.bannersColored}>
              <Container>
                <PromotionalBannerV2
                  page={SUBSCRIPTION_LIST}
                  bannerData={defaultPromotionalBannerDataV2?.bannerData}
                  onClick={handlePromotionalBannerClick}
                  showSpinner={isLoadingDoubleSubscription}
                />
              </Container>
            </div>
          )}
        </>
      )}
      {showPromotionalSubscriptionSidePanelV2 && (
        <DoubleSubscriptionPromotionalModalV2
          switchableSubscriptions={defaultPromotionalBannerDataV2?.targetSubscriptions || []}
          bannerData={defaultPromotionalBannerDataV2?.bannerData}
          isOpen={isPromotionalSidePanelV2Open}
          onClose={() => setIsPromotionalSidePanelV2Open(false)}
        />
      )}

      {/* expired or declined payment method modal */}
      <PaymentMethodModalV2
        mode="update-payment"
        paymentMethodId={problematicPaymentMethodId}
        isOpen={isEditPaymentModalV2Open}
        onClose={handleClosePaymentExpiredModalV2}
      />

      {/* missing payment method modal */}
      {/* currently there is no official way to trigger this from shopify flow */}
      <PaymentMethodModalV2
        mode="add-payment"
        isOpen={isAddPaymentMethodModalV2Open}
        onClose={handleCloseMissingPaymentMethodModalV2}
      />
      {!needToUpdatePayment && <ModalV53Notification />}
    </>
  );
};
